import {
  Box,
  Flex,
  Heading,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { ExpertRatingsPreview } from './ExpertRatingsPreview';
import { HERO_LEADERBOARD_PREVIEW_ROWS } from './fixtures/marketingPreviewData';
import type {
  ExpertRatingsPreviewRow,
  HeroLeaderboardPreviewRow,
} from './fixtures/marketingPreviewData';
import { PreviewDataStatusBadge } from './shared/PreviewDataStatusBadge';
import { RankByPrExplainer } from './shared/RankByPrExplainer';

type HeroTeaserRow = HeroLeaderboardPreviewRow & { profile_url?: string };

function toPreviewRow(
  row: HeroTeaserRow,
  rank: number,
): ExpertRatingsPreviewRow & { profile_url?: string } {
  return {
    rank,
    author_name: row.author_name,
    author_source: row.author_source,
    credibility_score: row.credibility_score,
    profit_rating: row.profit_rating,
    roi_pct: row.roi_pct,
    correct_picks: row.correct_picks,
    incorrect_picks: Math.max(0, row.total_picks - row.correct_picks),
    total_picks: row.total_picks,
    win_percentage: row.win_percentage,
    profile_url: row.profile_url,
  };
}

type HeroRatingsTeaserLiteProps = {
  rows?: HeroTeaserRow[];
  isLoading?: boolean;
};

export const HERO_TEASER_ROW_COUNT = 3;

/** PR-forward top-expert snapshot for the marketing homepage hero. */
export function HeroRatingsTeaserLite({
  rows: rowsProp,
  isLoading = false,
}: HeroRatingsTeaserLiteProps = {}) {
  const fallbackRows: HeroTeaserRow[] = HERO_LEADERBOARD_PREVIEW_ROWS.slice(
    0,
    HERO_TEASER_ROW_COUNT,
  );
  const teaserRows: HeroTeaserRow[] =
    rowsProp !== undefined
      ? rowsProp.slice(0, HERO_TEASER_ROW_COUNT)
      : fallbackRows;

  const previewRows = useMemo(
    () => teaserRows.map((row, index) => toPreviewRow(row, index + 1)),
    [teaserRows],
  );

  const panelBg = useColorModeValue('white', 'gray.800');
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.100');
  const titleColor = useColorModeValue('gray.900', 'white');

  if (isLoading) {
    return (
      <Flex
        w="100%"
        maxW="560px"
        mx="auto"
        minH="280px"
        align="center"
        justify="center"
        bg={panelBg}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={panelBorder}
      >
        <Spinner size="lg" color="primary.400" />
      </Flex>
    );
  }

  return (
    <Box
      w="100%"
      maxW="560px"
      mx="auto"
      bg={panelBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={panelBorder}
      boxShadow="lg"
      p={{ base: 4, md: 5 }}
      textAlign="left"
      minW={0}
      overflow="hidden"
    >
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'flex-start', sm: 'center' }}
        justify="space-between"
        gap={3}
        mb={3}
      >
        <Heading
          as="h2"
          fontSize={{ base: 'xl', md: '2xl' }}
          fontWeight="bold"
          color={titleColor}
          lineHeight="1.2"
          flex={1}
          minW={0}
        >
          Current Top 3 Experts
        </Heading>
        <PreviewDataStatusBadge kind="live" />
      </Flex>
      <RankByPrExplainer compact />
      <ExpertRatingsPreview
        rows={previewRows}
        maxRows={HERO_TEASER_ROW_COUNT}
        showHeading={false}
        prominentHeaders
        forceCards
        hideRankExplainer
        compact
      />
    </Box>
  );
}
